import { LoadingButton } from "@mui/lab";
import { Alert, Button, Chip, Icon, Stack, Typography } from "@mui/material";
import { useEffect, useReducer } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ErrorDetailContainer } from "../../../api/config";
import {
    AccountService,
    SmsCampaignDto,
    SmsCampaignService,
} from "../../../api/psc";
import { CardInformation, ErrorParser } from "../../../components/exports";
import PaperWithHeader from "../../../components/shared/PaperWithHeader";
import { RootState } from "../../../slices/Store";
import { ProjectSelectedFilterHelper } from "../../../utils/filter";
import {
    SmsCampaignState,
    SmsCampaignType,
    setInitialState,
    smsCampaignPrevStep,
} from "../smsCampaignSlice/SmsCampaignSlice";

type SmsCampaignDetailStateType = {
    error: ErrorDetailContainer | null;
    requirements: SmsCampaignRequirements | null;
    requestInProgress: boolean;
};

type SmsCampaignRequirements = {
    requiredPartCount: number;
    requiredSmsCount: number;
    availablePartCount: number;
};

const initialState: SmsCampaignDetailStateType = {
    error: null,
    requestInProgress: false,
    requirements: null,
};

type SmsCampaignDetailActionType =
    | {
          type: "set-error";
          payload: ErrorDetailContainer;
      }
    | {
          type: "clear-error";
      }
    | {
          type: "setRequirement";
          payload: SmsCampaignRequirements;
      }
    | {
          type: "setReqInProgressTrue";
      }
    | {
          type: "setReqInProgressFalse";
      };

const reducer = (
    state: SmsCampaignDetailStateType,
    action: SmsCampaignDetailActionType
) => {
    switch (action.type) {
        case "set-error":
            return { ...state, error: action.payload };
        case "clear-error":
            return { ...state, error: null };
        case "setRequirement":
            return { ...state, requirements: action.payload };
        case "setReqInProgressTrue":
            return { ...state, requestInProgress: true };
        case "setReqInProgressFalse":
            return { ...state, requestInProgress: false };
    }
};

// updated
export default function SmsCampaignDetail() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    //Getting required data from slice state
    const smsCampaign = useSelector((state: RootState) => state.smsCampaign);

    useEffect(() => {
        fetchRequirements();
    }, []);

    const fetchRequirements = async () => {
        const accountDetail = await AccountService.getAccountDetail("1");
        const { totalPartCount, totalSmsCount } =
            await createSmsCampaignWithDryRun(true, smsCampaign);
        localStateDispatch({
            type: "setRequirement",
            payload: {
                availablePartCount: accountDetail.partCount,
                requiredPartCount: totalPartCount,
                requiredSmsCount: totalSmsCount,
            },
        });
    };

    const createSmsCampaignWithDryRun = async (
        dryRun: boolean,
        state: SmsCampaignState
    ): Promise<SmsCampaignDto> => {
        localStateDispatch({
            type: "clear-error",
        });
        localStateDispatch({
            type: "setReqInProgressTrue",
        });

        try {
            const result = await SmsCampaignService.createSmsCampaign("1", {
                excelFileDestinations: state.excelFile ? [state.excelFile] : [],
                mobileNumberDestinations: [
                    { mobileNumbers: state.mobileNumbers },
                ],
                projectFilterDestinations: !state.projectFilter.targetAccessId
                    ? []
                    : [state.projectFilter],
                definition: { ...state.definition, dryRun: dryRun },
            });

            if (!dryRun) {
                navigate("/sms-list");
                dispatch(setInitialState());
            }

            return result;
        } catch (e) {
            localStateDispatch({ type: "set-error", payload: e });
        } finally {
            localStateDispatch({ type: "setReqInProgressFalse" });
        }
    };

    //Getting required data from shared state
    const { accessList, baseInfo: projectRelatedEntities } = useSelector(
        (state: RootState) => state.shared
    );

    //Initializing local state and reducer
    const [state, localStateDispatch] = useReducer(reducer, initialState);

    const handlePreviousStep = () => {
        dispatch(smsCampaignPrevStep());
    };

    // -----------------------------------------------------------------------------------------------------//
    // ----------------------------------------  business logic --------------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    const selectedAccess = accessList.find(
        (a) => a.id === smsCampaign.projectFilter.targetAccessId
    );

    const projectSelectedFilterHelper = new ProjectSelectedFilterHelper(
        projectRelatedEntities,
        accessList,
        smsCampaign.projectFilter,
        selectedAccess
    );

    const selectedCities = projectSelectedFilterHelper.getCities();
    const selectedRegions = projectSelectedFilterHelper.getSelectedRegions();
    const selectedSubRegions =
        projectSelectedFilterHelper.getSelectedSubRegions();
    const selectedPhases = projectSelectedFilterHelper.getSelectedPhases();

    const canCreateCampaign =
        state.requirements &&
        state.requirements.availablePartCount >=
            state.requirements.requiredPartCount;


    // -----------------------------------------------------------------------------------------------------//
    // -------------------------------------  business logic end -------------------------------------------//
    // -----------------------------------------------------------------------------------------------------//

    return (
        <Stack spacing={2}>
            <PaperWithHeader label="متن انتخابی">
                <Typography>{smsCampaign.definition.template}</Typography>
            </PaperWithHeader>

            {/* {accountDetailError && <Alert severity="error">{}</Alert>}
            {checkError && <Alert severity="error">{}</Alert>} */}

            {smsCampaign.smsCampaignType ===
                SmsCampaignType.CampaignWithProjectFilter && (
                <PaperWithHeader label={"فیلتر های انتخاب شده"}>
                    <Stack gap={1} alignItems="start">
                        {selectedCities.length > 0 && (
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {selectedCities.map((c) => (
                                    <Chip key={c.id} label={c.title} />
                                ))}
                            </Stack>
                        )}

                        {selectedRegions.length > 0 ? (
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {selectedRegions.map((r) => (
                                    <Chip key={r.id} label={r.title} />
                                ))}
                            </Stack>
                        ) : (
                            <Chip label="همه مناطق" />
                        )}

                        {selectedSubRegions.length > 0 && (
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {selectedSubRegions.map((r) => (
                                    <Chip key={r.id} label={r.title} />
                                ))}
                            </Stack>
                        )}

                        {selectedPhases.length > 0 ? (
                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {selectedPhases.map((p) => (
                                    <Chip key={p.id} label={p.title} />
                                ))}
                            </Stack>
                        ) : (
                            <Chip label="همه مراحل" />
                        )}
                    </Stack>
                </PaperWithHeader>
            )}

            {smsCampaign.mobileNumbers.length > 0 && (
                <PaperWithHeader label="شماره های انتخاب شده">
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {smsCampaign.mobileNumbers.map((pn) => (
                            <Chip key={pn} label={pn} />
                        ))}
                    </Stack>
                </PaperWithHeader>
            )}

            {smsCampaign.excelFile && (
                <PaperWithHeader label="فایل اکسل">
                    <Alert severity="info">
                        تعداد {smsCampaign.excelFile.totalCount} مخاطب از فایل
                        اکسل انتخاب شده است
                    </Alert>
                </PaperWithHeader>
            )}

            {state.requirements && (
                <PaperWithHeader label="جزییات کمپین پیامکی">
                    <Stack spacing={2}>
                        <CardInformation
                            title="تعداد پیامک"
                            value={state.requirements.requiredSmsCount}
                        />
                        <CardInformation
                            title="تعداد پارت"
                            value={state.requirements.requiredPartCount}
                        />
                        {!canCreateCampaign && (
                            <Alert
                                severity="error"
                                action={
                                    <Button onClick={fetchRequirements}>
                                        بررسی مجدد
                                    </Button>
                                }
                            >
                                شما برای این کمپین شارژ کافی ندارید
                            </Alert>
                        )}
                    </Stack>
                </PaperWithHeader>
            )}

            <ErrorParser error={state.error} />

            <Stack direction="row" justifyContent="space-between">
                <Button
                    onClick={handlePreviousStep}
                    variant="outlined"
                    startIcon={<Icon>navigate_next</Icon>}
                >
                    مرحله قبل
                </Button>

                <LoadingButton
                    loading={state.requestInProgress}
                    disabled={!canCreateCampaign}
                    onClick={() =>
                        createSmsCampaignWithDryRun(false, smsCampaign)
                    }
                    variant="contained"
                    color="secondary"
                    endIcon={<Icon>done</Icon>}
                >
                    نهایی سازی
                </LoadingButton>
            </Stack>
        </Stack>
    );
}
